import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  categoriesApi,
  itemsApi,
  locationsApi,
  type ItemInput,
} from '../api';
import { flattenTree } from '../lib/tree';
import { effectiveDefaultFields } from '../lib/categories';
import {
  CONDITIONS,
  activeFieldsFromValues,
  fieldsInGroup,
  type FieldGroupCode,
  type FieldName,
} from '../lib/field-groups';
import FieldGroupAccordion from '../components/FieldGroupAccordion';
import ItemMediaSection from '../components/ItemMediaSection';
import ItemLinksSection from '../components/ItemLinksSection';

const UNIT_OPTIONS = ['stk', 'rulle', 'pakke', 'sæt', 'm', 'cm', 'kg', 'g', 'l', 'ml'];

type ItemFormState = {
  name: string;
  description: string;
  categoryId: number | '';
  locationId: number | '';
  quantity: string;
  unit: string;
  lowStockThreshold: string;
  enabledFields: FieldName[];
  brand: string;
  model: string;
  serialNumber: string;
  purchaseLocation: string;
  acquiredAt: string;
  price: string;
  warrantyExpiresAt: string;
  insuranceValue: string;
  color: string;
  size: string;
  material: string;
  condition: string;
  conditionNote: string;
  author: string;
  publisher: string;
  publishedYear: string;
  isbn: string;
  registrationNumber: string;
  vin: string;
  modelYear: string;
};

const emptyState: ItemFormState = {
  name: '',
  description: '',
  categoryId: '',
  locationId: '',
  quantity: '1',
  unit: 'stk',
  lowStockThreshold: '',
  enabledFields: [],
  brand: '',
  model: '',
  serialNumber: '',
  purchaseLocation: '',
  acquiredAt: '',
  price: '',
  warrantyExpiresAt: '',
  insuranceValue: '',
  color: '',
  size: '',
  material: '',
  condition: '',
  conditionNote: '',
  author: '',
  publisher: '',
  publishedYear: '',
  isbn: '',
  registrationNumber: '',
  vin: '',
  modelYear: '',
};

export default function ItemFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = id !== undefined && id !== 'new';
  const itemId = isEdit ? Number(id) : null;

  const { data: existing, isLoading: loadingItem } = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => itemsApi.get(itemId!),
    enabled: itemId !== null,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.list,
  });

  const [s, setS] = useState<ItemFormState>(emptyState);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(!isEdit);
  const [fieldsExpanded, setFieldsExpanded] = useState(false);

  function update<K extends keyof ItemFormState>(key: K, value: ItemFormState[K]) {
    setS((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (!existing || hydrated) return;
    const explicitFields = (existing.enabledFields as FieldName[] | null) ?? null;
    const activeFields =
      explicitFields ?? activeFieldsFromValues(existing as unknown as Record<string, unknown>);
    setS({
      name: existing.name,
      description: existing.description ?? '',
      categoryId: existing.categoryId ?? '',
      locationId: existing.locationId ?? '',
      quantity: String(Number(existing.quantity)),
      unit: existing.unit,
      lowStockThreshold:
        existing.lowStockThreshold !== null ? String(Number(existing.lowStockThreshold)) : '',
      enabledFields: activeFields,
      brand: existing.brand ?? '',
      model: existing.model ?? '',
      serialNumber: existing.serialNumber ?? '',
      purchaseLocation: existing.purchaseLocation ?? '',
      acquiredAt: existing.acquiredAt ? existing.acquiredAt.slice(0, 10) : '',
      price: existing.priceCents !== null ? String(existing.priceCents / 100) : '',
      warrantyExpiresAt: existing.warrantyExpiresAt ? existing.warrantyExpiresAt.slice(0, 10) : '',
      insuranceValue:
        existing.insuranceValueCents !== null ? String(existing.insuranceValueCents / 100) : '',
      color: existing.color ?? '',
      size: existing.size ?? '',
      material: existing.material ?? '',
      condition: existing.condition ?? '',
      conditionNote: existing.conditionNote ?? '',
      author: existing.author ?? '',
      publisher: existing.publisher ?? '',
      publishedYear: existing.publishedYear !== null ? String(existing.publishedYear) : '',
      isbn: existing.isbn ?? '',
      registrationNumber: existing.registrationNumber ?? '',
      vin: existing.vin ?? '',
      modelYear: existing.modelYear !== null ? String(existing.modelYear) : '',
    });
    setHydrated(true);
  }, [existing, hydrated]);

  // Når kategori vælges på et nyt item: tilføj alle effective default-felter
  // (kategoriens egne + arvet fra forfædre) til de allerede valgte.
  useEffect(() => {
    if (isEdit) return;
    if (!s.categoryId) return;
    const defaults = effectiveDefaultFields(s.categoryId, categories);
    if (defaults.length === 0) return;
    setS((prev) => {
      const merged = new Set<FieldName>([...prev.enabledFields, ...defaults]);
      return { ...prev, enabledFields: [...merged] };
    });
  }, [s.categoryId, categories, isEdit]);

  const isFieldActive = (f: FieldName) => s.enabledFields.includes(f);
  const groupHasActive = (code: FieldGroupCode) =>
    fieldsInGroup(code).some((f) => isFieldActive(f));

  const saveMutation = useMutation({
    mutationFn: async (data: ItemInput) => {
      if (isEdit && itemId) return itemsApi.update(itemId, data);
      return itemsApi.create(data);
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['item'] });
      navigate(`/items/${saved.id}`);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'),
      );
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const text = (active: boolean, v: string) => (active ? (v ? v : null) : null);
    const num = (active: boolean, v: string) => (active && v !== '' ? Number(v) : null);
    const cents = (active: boolean, v: string) =>
      active && v !== '' ? Math.round(Number(v) * 100) : null;

    const data: ItemInput = {
      name: s.name,
      description: s.description || null,
      categoryId: s.categoryId || null,
      locationId: s.locationId || null,
      quantity: Number(s.quantity),
      unit: s.unit,
      lowStockThreshold:
        isFieldActive('lowStockThreshold') && s.lowStockThreshold
          ? Number(s.lowStockThreshold)
          : null,
      enabledFields: s.enabledFields,
      brand: text(isFieldActive('brand'), s.brand),
      model: text(isFieldActive('model'), s.model),
      serialNumber: text(isFieldActive('serialNumber'), s.serialNumber),
      purchaseLocation: text(isFieldActive('purchaseLocation'), s.purchaseLocation),
      acquiredAt: isFieldActive('acquiredAt') && s.acquiredAt ? s.acquiredAt : null,
      priceCents: cents(isFieldActive('priceCents'), s.price),
      warrantyExpiresAt:
        isFieldActive('warrantyExpiresAt') && s.warrantyExpiresAt ? s.warrantyExpiresAt : null,
      insuranceValueCents: cents(isFieldActive('insuranceValueCents'), s.insuranceValue),
      color: text(isFieldActive('color'), s.color),
      size: text(isFieldActive('size'), s.size),
      material: text(isFieldActive('material'), s.material),
      condition: isFieldActive('condition') ? s.condition || null : null,
      conditionNote: text(isFieldActive('conditionNote'), s.conditionNote),
      author: text(isFieldActive('author'), s.author),
      publisher: text(isFieldActive('publisher'), s.publisher),
      publishedYear: num(isFieldActive('publishedYear'), s.publishedYear),
      isbn: text(isFieldActive('isbn'), s.isbn),
      registrationNumber: text(isFieldActive('registrationNumber'), s.registrationNumber),
      vin: text(isFieldActive('vin'), s.vin),
      modelYear: num(isFieldActive('modelYear'), s.modelYear),
    };
    saveMutation.mutate(data);
  }

  if (loadingItem) {
    return <div className="text-center text-slate-500 py-8">{t('common.loading')}</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {isEdit ? t('items.editTitle') : t('items.newTitle')}
        </h1>
      </div>

      {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      {/* Grundfelter */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.name')}</span>
          <input
            type="text"
            required
            maxLength={200}
            autoFocus
            value={s.name}
            onChange={(e) => update('name', e.target.value)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.description')}</span>
          <textarea
            rows={3}
            maxLength={5000}
            value={s.description}
            onChange={(e) => update('description', e.target.value)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">{t('field.category')}</span>
            <select
              value={s.categoryId}
              onChange={(e) => update('categoryId', e.target.value ? Number(e.target.value) : '')}
              className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
            >
              <option value="">{t('field.none')}</option>
              {flattenTree(categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {'— '.repeat(c.depth)}
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">{t('field.location')}</span>
            <select
              value={s.locationId}
              onChange={(e) => update('locationId', e.target.value ? Number(e.target.value) : '')}
              className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
            >
              <option value="">{t('field.none')}</option>
              {flattenTree(locations).map((l) => (
                <option key={l.id} value={l.id}>
                  {'— '.repeat(l.depth)}
                  {l.name}
                </option>
              ))}
            </select>
          </label>
        </div>

      </div>

      {/* Lagerstyring som dens egen gruppe-card — hvert felt vises individuelt */}
      {groupHasActive('inventory') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.inventory')}
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {isFieldActive('quantity') && (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">{t('field.quantity')}</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={s.quantity}
                  onChange={(e) => update('quantity', e.target.value)}
                  className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
            )}
            {isFieldActive('unit') && (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">{t('field.unit')}</span>
                <select
                  value={s.unit}
                  onChange={(e) => update('unit', e.target.value)}
                  className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
                >
                  {UNIT_OPTIONS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {isFieldActive('lowStockThreshold') && (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">
                  {t('field.lowStockThreshold')}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={s.lowStockThreshold}
                  onChange={(e) => update('lowStockThreshold', e.target.value)}
                  placeholder={t('field.optional')}
                  className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
            )}
          </div>
        </div>
      )}

      {/* Identifikation */}
      {groupHasActive('identification') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.identification')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {isFieldActive('brand') && (
              <TextField
                label={t('field.brand')}
                value={s.brand}
                onChange={(v) => update('brand', v)}
              />
            )}
            {isFieldActive('model') && (
              <TextField
                label={t('field.model')}
                value={s.model}
                onChange={(v) => update('model', v)}
              />
            )}
          </div>
          {isFieldActive('serialNumber') && (
            <TextField
              label={t('field.serialNumber')}
              value={s.serialNumber}
              onChange={(v) => update('serialNumber', v)}
            />
          )}
        </div>
      )}

      {/* Køb og garanti */}
      {groupHasActive('purchase') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.purchase')}
          </h3>
          {isFieldActive('purchaseLocation') && (
            <TextField
              label={t('field.purchaseLocation')}
              value={s.purchaseLocation}
              onChange={(v) => update('purchaseLocation', v)}
            />
          )}
          {(isFieldActive('acquiredAt') || isFieldActive('priceCents')) && (
            <div className="grid grid-cols-2 gap-4">
              {isFieldActive('acquiredAt') && (
                <DateField
                  label={t('field.acquiredAt')}
                  value={s.acquiredAt}
                  onChange={(v) => update('acquiredAt', v)}
                />
              )}
              {isFieldActive('priceCents') && (
                <PriceField
                  label={t('field.price')}
                  value={s.price}
                  onChange={(v) => update('price', v)}
                />
              )}
            </div>
          )}
          {(isFieldActive('warrantyExpiresAt') || isFieldActive('insuranceValueCents')) && (
            <div className="grid grid-cols-2 gap-4">
              {isFieldActive('warrantyExpiresAt') && (
                <DateField
                  label={t('field.warrantyExpiresAt')}
                  value={s.warrantyExpiresAt}
                  onChange={(v) => update('warrantyExpiresAt', v)}
                />
              )}
              {isFieldActive('insuranceValueCents') && (
                <PriceField
                  label={t('field.insuranceValue')}
                  value={s.insuranceValue}
                  onChange={(v) => update('insuranceValue', v)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Udseende */}
      {groupHasActive('appearance') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.appearance')}
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {isFieldActive('color') && (
              <TextField
                label={t('field.color')}
                value={s.color}
                onChange={(v) => update('color', v)}
              />
            )}
            {isFieldActive('size') && (
              <TextField
                label={t('field.size')}
                value={s.size}
                onChange={(v) => update('size', v)}
              />
            )}
            {isFieldActive('material') && (
              <TextField
                label={t('field.material')}
                value={s.material}
                onChange={(v) => update('material', v)}
              />
            )}
          </div>
        </div>
      )}

      {/* Tilstand */}
      {groupHasActive('condition') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.condition')}
          </h3>
          {isFieldActive('condition') && (
            <label className="block">
              <span className="text-sm font-medium text-slate-700">{t('field.condition')}</span>
              <select
                value={s.condition}
                onChange={(e) => update('condition', e.target.value)}
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
              >
                <option value="">{t('field.none')}</option>
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`condition.${c}`)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isFieldActive('conditionNote') && (
            <label className="block">
              <span className="text-sm font-medium text-slate-700">{t('field.conditionNote')}</span>
              <textarea
                rows={2}
                maxLength={2000}
                value={s.conditionNote}
                onChange={(e) => update('conditionNote', e.target.value)}
                placeholder={t('field.optional')}
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
          )}
        </div>
      )}

      {/* Bøger og medier */}
      {groupHasActive('books') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.books')}
          </h3>
          {(isFieldActive('author') || isFieldActive('publisher')) && (
            <div className="grid grid-cols-2 gap-4">
              {isFieldActive('author') && (
                <TextField
                  label={t('field.author')}
                  value={s.author}
                  onChange={(v) => update('author', v)}
                />
              )}
              {isFieldActive('publisher') && (
                <TextField
                  label={t('field.publisher')}
                  value={s.publisher}
                  onChange={(v) => update('publisher', v)}
                />
              )}
            </div>
          )}
          {(isFieldActive('publishedYear') || isFieldActive('isbn')) && (
            <div className="grid grid-cols-2 gap-4">
              {isFieldActive('publishedYear') && (
                <NumberField
                  label={t('field.publishedYear')}
                  value={s.publishedYear}
                  onChange={(v) => update('publishedYear', v)}
                  min={0}
                  max={9999}
                />
              )}
              {isFieldActive('isbn') && (
                <TextField
                  label={t('field.isbn')}
                  value={s.isbn}
                  onChange={(v) => update('isbn', v)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Køretøj */}
      {groupHasActive('vehicle') && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase">
            {t('fieldGroups.vehicle')}
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {isFieldActive('registrationNumber') && (
              <TextField
                label={t('field.registrationNumber')}
                value={s.registrationNumber}
                onChange={(v) => update('registrationNumber', v)}
              />
            )}
            {isFieldActive('vin') && (
              <TextField label={t('field.vin')} value={s.vin} onChange={(v) => update('vin', v)} />
            )}
            {isFieldActive('modelYear') && (
              <NumberField
                label={t('field.modelYear')}
                value={s.modelYear}
                onChange={(v) => update('modelYear', v)}
                min={0}
                max={9999}
              />
            )}
          </div>
        </div>
      )}

      {/* Felt-vælger (collapsible — under gruppe-cards så brugeren først ser
          de allerede-valgte felter, og kan tilføje flere ved at klikke) */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => setFieldsExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 p-4 hover:bg-slate-50 text-left"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-base text-slate-700 font-medium">
                <span className="inline-block w-5 text-center">+</span> {t('items.addFields')}
              </span>
              {s.enabledFields.length > 0 && (
                <span className="text-xs text-slate-500">
                  ({t('items.fieldsActive', { count: s.enabledFields.length })})
                </span>
              )}
            </div>
            {!fieldsExpanded && (
              <p className="text-xs text-slate-500 mt-0.5 ml-7">{t('items.addFieldsHint')}</p>
            )}
          </div>
          <Chevron open={fieldsExpanded} />
        </button>
        {fieldsExpanded && (
          <div className="px-4 pb-4">
            <FieldGroupAccordion
              selected={s.enabledFields}
              onChange={(fields) => update('enabledFields', fields)}
            />
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <Link to="/items" className="text-sm text-slate-600 hover:text-slate-900 hover:underline">
          {t('common.cancel')}
        </Link>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="bg-slate-900 text-white rounded px-6 py-2 hover:bg-slate-800 text-sm font-medium disabled:opacity-50"
        >
          {saveMutation.isPending ? t('common.working') : t('common.save')}
        </button>
      </div>

      {isEdit && itemId !== null && (
        <div className="space-y-4 pt-4 border-t border-slate-200">
          <ItemMediaSection itemId={itemId} kind="photo" />
          <ItemMediaSection itemId={itemId} kind="receipt" />
          <ItemLinksSection itemId={itemId} />
        </div>
      )}

      {!isEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
          {t('items.mediaAfterSave')}
        </div>
      )}
    </form>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
      />
    </label>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
      />
    </label>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 16"
      fill="none"
      className={`text-slate-500 transition-transform ${open ? 'rotate-90' : ''} flex-shrink-0`}
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PriceField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1 flex">
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-l border border-slate-300 px-3 py-2"
        />
        <span className="inline-flex items-center px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r text-sm text-slate-600">
          DKK
        </span>
      </div>
    </label>
  );
}
